import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Wallet, Pencil } from "lucide-react";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import { PageHeader, PageShell } from "@/components/PageShell";
import { PageEmptyState, PageLoadingState } from "@/components/PageStates";
import { useAuth } from "@/contexts/AuthContext";
import { getTodayInBusinessTz } from "@/lib/date";
import { formatRupiah } from "@/lib/currency";
import { MODAL_MANAGE_ROLES } from "@/lib/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HISTORY_PAGE_SIZE = 5;

const getCurrentPeriod = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const formatThousandsInput = (value) => {
  const digitsOnly = String(value ?? "").replace(/\D/g, "");
  if (!digitsOnly) {
    return "";
  }
  return Number(digitsOnly).toLocaleString("id-ID");
};

const parseThousandsInput = (value) => {
  const digitsOnly = String(value ?? "").replace(/\D/g, "");
  if (!digitsOnly) {
    return NaN;
  }
  return Number(digitsOnly);
};

const ModalPage = () => {
  const { user } = useAuth();
  const [modal, setModal] = useState([]);
  const [ringkasan, setRingkasan] = useState(null);
  const [bukuKas, setBukuKas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingModalId, setEditingModalId] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentPeriod());
  const [modalOffset, setModalOffset] = useState(0);
  const [totalModal, setTotalModal] = useState(0);
  const [hasMoreModal, setHasMoreModal] = useState(false);
  const [loadingMoreModal, setLoadingMoreModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Ya",
    onConfirm: null,
  });
  const [formData, setFormData] = useState({
    modal_awal: "",
    tanggal: getTodayInBusinessTz(),
    catatan: "",
  });
  const canManageModal = MODAL_MANAGE_ROLES.includes(user?.role);
  const canViewModalHistory = canManageModal;

  const fetchModalHistory = useCallback(async ({ append = false, baseOffset = 0 } = {}) => {
    if (!canViewModalHistory) {
      setModal([]);
      setModalOffset(0);
      setTotalModal(0);
      setHasMoreModal(false);
      return;
    }

    const nextOffset = append ? baseOffset : 0;
    setLoadingMoreModal(true);
    try {
      const modalRes = await axios.get(`${API}/modal`, {
        params: {
          limit: HISTORY_PAGE_SIZE,
          offset: nextOffset,
        },
      });
      const rows = modalRes.data || [];
      const totalFromHeader = Number(modalRes.headers?.["x-total-count"]);
      setModal((prev) => (append ? [...prev, ...rows] : rows));
      const updatedOffset = nextOffset + rows.length;
      setModalOffset(updatedOffset);
      if (Number.isFinite(totalFromHeader)) {
        setTotalModal(totalFromHeader);
        setHasMoreModal(updatedOffset < totalFromHeader);
      } else {
        setHasMoreModal(rows.length === HISTORY_PAGE_SIZE);
      }
    } catch (error) {
      toast.error("Gagal memuat riwayat modal");
    } finally {
      setLoadingMoreModal(false);
    }
  }, [canViewModalHistory]);

  const fetchData = useCallback(async (periodValue = selectedPeriod) => {
    const [tahunStr, bulanStr] = String(periodValue || "").split("-");
    const tahun = Number(tahunStr);
    const bulan = Number(bulanStr);
    try {
      const [ringkasanRes, bukuKasRes] = await Promise.all([
        axios.get(`${API}/modal/ringkasan`),
        axios.get(`${API}/modal/buku-kas`, {
          params: {
            tahun: Number.isFinite(tahun) ? tahun : undefined,
            bulan: Number.isFinite(bulan) ? bulan : undefined,
          },
        }),
      ]);
      setRingkasan(ringkasanRes.data);
      setBukuKas(bukuKasRes.data);
      if (canViewModalHistory) {
        await fetchModalHistory({ append: false, baseOffset: 0 });
      } else {
        setModal([]);
        setModalOffset(0);
        setTotalModal(0);
        setHasMoreModal(false);
      }
    } catch (error) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, fetchModalHistory, canViewModalHistory]);

  useEffect(() => {
    fetchData(selectedPeriod);
  }, [fetchData, selectedPeriod]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const modalAwalNumber = parseThousandsInput(formData.modal_awal);
    if (!Number.isFinite(modalAwalNumber) || modalAwalNumber <= 0) {
      toast.error("Modal awal harus lebih dari 0");
      return;
    }
    const payload = {
      ...formData,
      modal_awal: modalAwalNumber,
    };
    setConfirmDialog({
      open: true,
      title: editMode ? "Konfirmasi Perbarui Modal" : "Konfirmasi Simpan Modal",
      description: editMode
        ? "Apakah Anda yakin ingin memperbarui data modal ini?"
        : "Apakah Anda yakin ingin menyimpan data modal ini?",
      confirmText: editMode ? "Ya, Perbarui" : "Ya, Simpan",
      onConfirm: async () => {
        try {
          if (editMode && editingModalId) {
            await axios.put(`${API}/modal/${editingModalId}`, payload);
            toast.success("Modal berhasil diupdate");
          } else {
            await axios.post(`${API}/modal`, payload);
            toast.success("Modal berhasil ditambahkan");
          }
          handleCloseDialog();
          fetchData(selectedPeriod);
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menyimpan data");
        }
      },
    });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setEditingModalId(null);
    setFormData({
      modal_awal: "",
      tanggal: getTodayInBusinessTz(),
      catatan: "",
    });
  };

  const handleOpenAddDialog = async () => {
    setEditMode(false);
    setEditingModalId(null);
    let defaultModalAwal = "";
    try {
      const res = await axios.get(`${API}/modal/default-awal`);
      defaultModalAwal = formatThousandsInput(res.data?.default_modal_awal);
    } catch (error) {
      defaultModalAwal = "";
    }
    setFormData({
      modal_awal: defaultModalAwal,
      tanggal: getTodayInBusinessTz(),
      catatan: "",
    });
    setDialogOpen(true);
  };

  const handleEditModal = (item) => {
    setEditMode(true);
    setEditingModalId(item.modal_id);
    setFormData({
      modal_awal: formatThousandsInput(item.modal_awal),
      tanggal: String(item.tanggal).slice(0, 10),
      catatan: item.catatan || "",
    });
    setDialogOpen(true);
  };

  if (loading) {
    return <PageLoadingState label="Memuat data modal" description="Mengambil ringkasan modal dan buku kas operasional dapur." />;
  }

  return (
    <PageShell data-testid="modal-page">
      <PageHeader
        eyebrow="Keuangan Operasional"
        title={canManageModal ? "Manajemen Modal" : "Ringkasan Modal"}
        description={canManageModal
          ? "Pantau modal awal, pengurang modal, dan sisa modal dapur dalam susunan halaman yang lebih rapi."
          : "Lihat total uang tersedia dan buku kas operasional dapur tanpa akses menambah atau mengubah data modal."}
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="month"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-44"
            />
            {canManageModal ? (
              <Button
                data-testid="add-modal-button"
                onClick={handleOpenAddDialog}
              >
                <Plus size={16} className="mr-2" />
                Tambah Modal
              </Button>
            ) : null}
          </div>
        )}
      />

        {canViewModalHistory && modal.length > 0 && (
          <div className="p-4 border-t">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={!hasMoreModal || loadingMoreModal}
              onClick={() => fetchModalHistory({ append: true, baseOffset: modalOffset })}
            >
              {loadingMoreModal
                ? "Memuat..."
                : hasMoreModal
                  ? "Muat Lebih Banyak"
                  : "Semua data sudah ditampilkan"}
            </Button>
          </div>
        )}
      

      {canManageModal ? (
        <>
          <div className="rounded-lg border bg-card p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-primary/10 text-primary p-3 rounded-lg">
                <Wallet size={24} />
              </div>
              <div>
                <p className="text-sm font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                  Modal Terakhir
                </p>
                <p className="text-2xl font-manrope font-bold text-primary">
                  {formatRupiah(ringkasan?.sisa_modal ?? modal[0]?.sisa_modal ?? 0)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-sm text-muted-foreground">Total Modal Ditambahkan</p>
                <p className="font-manrope font-bold">
                  {formatRupiah(ringkasan?.total_modal_ditambahkan ?? modal[0]?.modal_awal ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Belanja Pengurang Modal</p>
                <p className="font-manrope font-bold text-destructive">
                  {formatRupiah(ringkasan?.total_belanja_masuk ?? modal[0]?.total_belanja_masuk ?? 0)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 mt-4 border-t">
              <div>
                <p className="text-sm text-muted-foreground">Total Belanja Bruto</p>
                <p className="font-manrope font-bold">
                  {formatRupiah(ringkasan?.total_belanja_masuk_bruto ?? modal[0]?.total_belanja_masuk_bruto ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Belanja Dikecualikan Modal</p>
                <p className="font-manrope font-bold text-amber-600">
                  {formatRupiah(ringkasan?.total_belanja_dikecualikan_modal ?? modal[0]?.total_belanja_dikecualikan_modal ?? 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Kategori Dikecualikan</p>
                <p className="font-manrope font-bold">
                  {(ringkasan?.kategori_dikecualikan_modal || modal[0]?.kategori_dikecualikan_modal || []).join(", ") || "-"}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-card overflow-hidden">
            {modal.length > 0 && (
              <div className="px-4 pt-4 text-sm text-muted-foreground">
                Menampilkan {modal.length}{totalModal > 0 ? ` dari ${totalModal}` : ""} data
              </div>
            )}
            {modal.length === 0 ? (
              <div className="p-4">
                <PageEmptyState
                  compact
                  title="Belum ada data modal"
                  description="Tambahkan modal untuk mulai memantau saldo dan pengeluaran operasional dapur."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                        Tanggal
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                        Modal Awal
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                        Total Belanja
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                        Belanja Dikecualikan
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                        Sisa Modal
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                        Catatan
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                        Aksi
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {modal.map((item) => (
                      <tr
                        key={item.modal_id}
                        data-testid={`modal-row-${item.modal_id}`}
                        className="border-b transition-colors hover:bg-muted/50"
                      >
                        <td className="px-4 py-4">
                          {new Date(item.tanggal).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-4 py-4 font-manrope font-bold">
                          {formatRupiah(item.modal_awal)}
                        </td>
                        <td className="px-4 py-4 font-manrope font-bold text-destructive">
                          {formatRupiah(item.total_belanja_masuk)}
                        </td>
                        <td className="px-4 py-4 font-manrope font-bold text-amber-600">
                          {formatRupiah(item.total_belanja_dikecualikan_modal || 0)}
                        </td>
                        <td className="px-4 py-4 font-manrope font-bold text-primary">
                          {formatRupiah(item.sisa_modal)}
                        </td>
                        <td className="px-4 py-4 text-muted-foreground">
                          {item.catatan || "-"}
                        </td>
                        <td className="px-4 py-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditModal(item)}
                          >
                            <Pencil size={14} className="mr-1" />
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 text-primary p-3 rounded-lg">
              <Wallet size={24} />
            </div>
            <div>
              <p className="text-sm font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                Total Uang Tersedia
              </p>
              <p className="text-2xl font-manrope font-bold text-primary">
                {formatRupiah(ringkasan?.sisa_modal ?? 0)}
              </p>
            </div>
          </div>
          <p className="mt-4 border-t pt-4 text-sm text-muted-foreground">
            Role pengawas hanya dapat melihat saldo uang tersedia dan buku kas per bulan tanpa akses riwayat atau perubahan data modal.
          </p>
        </div>
      )}

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/40 flex items-center justify-between">
          <h3 className="font-manrope font-bold text-lg">Buku Kas</h3>
          <p className="text-sm text-muted-foreground">
            Periode {bukuKas?.periode || selectedPeriod} (WITA)
          </p>
        </div>
        {bukuKas?.rows?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                  No
                </th>
                <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                  Tanggal
                </th>
                <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                  Uraian Transaksi
                </th>
                <th className="px-4 py-3 text-right text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                  Masuk
                </th>
                <th className="px-4 py-3 text-right text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                  Keluar
                </th>
                <th className="px-4 py-3 text-right text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
                  Saldo
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-muted/20">
                <td className="px-4 py-3 font-medium" colSpan={5}>
                  Saldo Awal Periode
                </td>
                <td className="px-4 py-3 text-right font-manrope font-bold">
                  {formatRupiah(bukuKas?.saldo_awal_periode || 0)}
                </td>
              </tr>
              {bukuKas.rows.map((row, idx) => (
                  <tr
                    key={`${row.tanggal}-${idx}`}
                    data-testid={`buku-kas-row-${idx}`}
                    className="border-b transition-colors hover:bg-muted/50"
                  >
                    <td className="px-4 py-3">{idx + 1}</td>
                    <td className="px-4 py-3">
                      {new Date(`${row.tanggal}T00:00:00+08:00`).toLocaleDateString("id-ID", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3">{row.uraian_transaksi}</td>
                    <td className="px-4 py-3 text-right font-manrope font-semibold text-green-700">
                      {formatRupiah(row.masuk || 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-manrope font-semibold text-destructive">
                      {formatRupiah(row.keluar || 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-manrope font-bold text-primary">
                      {formatRupiah(row.saldo || 0)}
                    </td>
                  </tr>
                ))}
              <tr className="bg-muted/20">
                <td className="px-4 py-3 font-medium" colSpan={3}>
                  Total Periode
                </td>
                <td className="px-4 py-3 text-right font-manrope font-bold text-green-700">
                  {formatRupiah(bukuKas?.total_masuk || 0)}
                </td>
                <td className="px-4 py-3 text-right font-manrope font-bold text-destructive">
                  {formatRupiah(bukuKas?.total_keluar || 0)}
                </td>
                <td className="px-4 py-3 text-right font-manrope font-bold text-primary">
                  {formatRupiah(bukuKas?.saldo_akhir || 0)}
                </td>
              </tr>
            </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 pt-0">
            <PageEmptyState
              compact
              title="Tidak ada data buku kas"
              description="Belum ada mutasi buku kas pada periode yang dipilih."
            />
          </div>
        )}
      </div>

      {canManageModal ? (
        <>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleCloseDialog();
                return;
              }
              setDialogOpen(true);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editMode ? "Edit Modal" : "Tambah Modal"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Modal Awal</Label>
                  <Input
                    data-testid="form-modal-awal"
                    type="text"
                    inputMode="numeric"
                    placeholder="Masukkan nominal modal"
                    value={formData.modal_awal}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        modal_awal: formatThousandsInput(e.target.value),
                      })
                    }
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    Modal baru menambah total modal akumulatif. Belanja kategori Susu & Gas tidak mengurangi modal.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Tanggal Modal</Label>
                  <Input
                    data-testid="form-tanggal-modal"
                    type="date"
                    value={formData.tanggal}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tanggal: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Catatan (Opsional)</Label>
                  <Textarea
                    data-testid="form-catatan"
                    value={formData.catatan}
                    onChange={(e) =>
                      setFormData({ ...formData, catatan: e.target.value })
                    }
                    rows={3}
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseDialog}
                  >
                    Batal
                  </Button>
                  <Button data-testid="submit-modal-button" type="submit">
                    {editMode ? "Update" : "Simpan"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <ConfirmActionDialog
            open={confirmDialog.open}
            onOpenChange={(open) =>
              setConfirmDialog((prev) => ({ ...prev, open, onConfirm: open ? prev.onConfirm : null }))
            }
            title={confirmDialog.title}
            description={confirmDialog.description}
            confirmText={confirmDialog.confirmText}
            onConfirm={async () => {
              if (confirmDialog.onConfirm) {
                await confirmDialog.onConfirm();
              }
              setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
            }}
          />
        </>
      ) : null}
    </PageShell>
  );
};

export default ModalPage;
